/**
 * =============================================================================
 * CustomizeSidebarMenuDialog — Personalização da barra de navegação
 * Versão: 1.0.0 (2026-09-07)
 *
 * Descrição:
 *   Modal aberto pela opção "Personalizar menu" (popover do botão "Mais").
 *   Permite escolher quais menus pais aparecem no rail lateral:
 *     - Menus fixos (Dashboard, Chat, CRM, Kanban): sempre marcados e
 *       bloqueados, nas posições atuais;
 *     - Demais menus: marcáveis, limitados a `maxOpcionais` (padrão 4) para
 *       não estourar a altura do rail e forçar barra de rolagem.
 *
 *   Leiaute reaproveita o `WorkflowDialogShell`, que replica o padrão visual
 *   do modal de anotações do chat (header com gradiente + tile de ícone,
 *   corpo rolável e footer compacto).
 *
 * CHANGELOG:
 *   v1.0.0 (2026-09-07) - Versão inicial.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, SlidersHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { WorkflowDialogShell } from '@/components/workflow/WorkflowDialogShell';
import { cn } from '@/lib/utils';
import type { NavCategory } from '@/components/navigation/navRegistry';

interface CustomizeSidebarMenuDialogProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Todas as categorias visíveis para o usuário (já filtradas por permissão). */
  categories: NavCategory[];
  /** Ids dos menus sempre fixos (não desmarcáveis). */
  fixedIds: string[];
  /** Ids dos menus opcionais atualmente visíveis no rail. */
  selectedIds: string[];
  /** Limite de menus opcionais simultâneos. */
  maxOpcionais: number;
  /** Persiste a nova seleção de menus opcionais. */
  onSave: (ids: string[]) => void;
}

export function CustomizeSidebarMenuDialog({
  open,
  onOpenChange,
  categories,
  fixedIds,
  selectedIds,
  maxOpcionais,
  onSave,
}: CustomizeSidebarMenuDialogProps) {
  const { t } = useTranslation('sidebar');

  // Rascunho local: só é aplicado ao salvar.
  const [rascunho, setRascunho] = useState<string[]>(selectedIds);

  // Reidrata o rascunho a cada abertura do modal.
  useEffect(() => {
    if (open) setRascunho(selectedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fixos primeiro (na ordem informada), depois os demais na ordem do registro.
  const { fixos, opcionais } = useMemo(() => {
    const fixas = fixedIds
      .map((id) => categories.find((c) => c.id === id))
      .filter((c): c is NavCategory => !!c);
    const outras = categories.filter((c) => !fixedIds.includes(c.id));
    return { fixos: fixas, opcionais: outras };
  }, [categories, fixedIds]);

  const limiteAtingido = rascunho.length >= maxOpcionais;

  // Comparação simples de conjuntos para habilitar "Salvar alterações".
  const houveMudanca =
    rascunho.length !== selectedIds.length ||
    rascunho.some((id) => !selectedIds.includes(id));

  /** Marca/desmarca um menu opcional respeitando o limite. */
  const alternar = (id: string) => {
    setRascunho((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxOpcionais) return prev;
      return [...prev, id];
    });
  };

  /** Linha da lista (checkbox + ícone + rótulo). */
  const renderLinha = (
    cat: NavCategory,
    opts: { fixo: boolean; marcado: boolean; desabilitado: boolean },
  ) => {
    const Icon = cat.icon;
    const conteudo = (
      <div
        className={cn(
          'flex items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 transition-colors',
          opts.fixo ? 'bg-muted/30' : 'hover:bg-muted/40',
          opts.desabilitado && !opts.fixo && 'opacity-50',
        )}
      >
        <Checkbox
          checked={opts.marcado}
          disabled={opts.fixo || opts.desabilitado}
          onCheckedChange={() => !opts.fixo && alternar(cat.id)}
        />
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate text-sm text-foreground">
          {t(cat.titleKey, cat.defaultLabel)}
        </span>
        {opts.fixo && <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </div>
    );

    if (!opts.fixo) {
      return (
        <button
          key={cat.id}
          type="button"
          className="w-full text-left"
          disabled={opts.desabilitado}
          onClick={() => alternar(cat.id)}
        >
          {conteudo}
        </button>
      );
    }

    return (
      <Tooltip key={cat.id}>
        <TooltipTrigger asChild>
          <div className="w-full cursor-default">{conteudo}</div>
        </TooltipTrigger>
        <TooltipContent side="top">Sempre fixado na barra lateral</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <WorkflowDialogShell
      open={open}
      onOpenChange={onOpenChange}
      icon={SlidersHorizontal}
      title="Personalização da barra de navegação"
      description="Escolha quais elementos vão aparecer na barra lateral."
      maxWidthClass="sm:max-w-lg"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={!houveMudanca}
            onClick={() => {
              onSave(rascunho);
              onOpenChange(false);
            }}
          >
            Salvar alterações
          </Button>
        </>
      }
    >
      <TooltipProvider delayDuration={200}>
        <div className="flex flex-col gap-1">
          {fixos.map((cat) =>
            renderLinha(cat, { fixo: true, marcado: true, desabilitado: true }),
          )}

          {opcionais.map((cat) => {
            const marcado = rascunho.includes(cat.id);
            return renderLinha(cat, {
              fixo: false,
              marcado,
              desabilitado: !marcado && limiteAtingido,
            });
          })}
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          Você pode escolher até {maxOpcionais} menus além dos fixos. Os demais
          continuam disponíveis no botão "Mais".
        </p>
      </TooltipProvider>
    </WorkflowDialogShell>
  );
}
